import React, { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  BIMESTRES_PADRAO, bimestreDaData, periodoDoBimestre, ultimoDiaDoMes,
} from '../utils/bimestres';

const MODOS = [
  { id: 'bimestre', label: 'Bimestre' },
  { id: 'mes', label: 'Mês' },
  { id: 'datas', label: 'Datas' },
];

const SITUACOES = [
  { value: '', label: 'Todas as aulas' },
  { value: 'concluidas', label: 'Somente concluídas' },
  { value: 'pendentes', label: 'Somente pendentes' },
  { value: 'nao_realizadas', label: 'Somente não realizadas' },
];

function ExportarRegistroAulasModal({
  aberto,
  subjects,
  disciplinaInicial,
  anoLetivo,
  mesInicial,
  gerando = false,
  onExportar,
  onFechar,
}) {
  const { darkMode } = useTheme();
  const hoje = new Date().toISOString().slice(0, 10);

  const [modo, setModo] = useState('bimestre');
  const [mes, setMes] = useState(mesInicial);
  const [bimestre, setBimestre] = useState(1);
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [disciplina, setDisciplina] = useState(disciplinaInicial || '');
  const [situacao, setSituacao] = useState('');
  const [erro, setErro] = useState(null);

  const escolherBimestre = (numero) => {
    setBimestre(numero);
    const p = periodoDoBimestre(numero, anoLetivo);
    setInicio(p.inicio);
    setFim(p.fim);
  };

  // Cada abertura parte do que está na tela: a disciplina escolhida e o
  // bimestre em que cai o mês do calendário.
  useEffect(() => {
    if (!aberto) return;
    setDisciplina(disciplinaInicial || '');
    setMes(mesInicial);
    setSituacao('');
    setErro(null);
    setModo('bimestre');
    escolherBimestre(bimestreDaData(`${mesInicial}-15`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  if (!aberto) return null;

  const trocarModo = (novo) => {
    setErro(null);
    setModo(novo);
    if (novo === 'bimestre') escolherBimestre(bimestre);
    if (novo === 'datas') {
      const [a, m] = mes.split('-');
      setInicio(`${a}-${m}-01`);
      setFim(ultimoDiaDoMes(a, m));
    }
  };

  const exportar = () => {
    const params = {};
    if (disciplina) params.id_subject = disciplina;
    if (situacao) params.status = situacao;
    if (modo === 'mes') {
      if (!mes) { setErro('Escolha o mês.'); return; }
      params.month = mes;
    } else {
      if (!inicio || !fim) { setErro('Preencha a data inicial e a final.'); return; }
      if (inicio > fim) { setErro('A data inicial não pode ser depois da final.'); return; }
      params.start_date = inicio;
      params.end_date = fim;
      if (modo === 'bimestre') params.period_label = `${bimestre}º Bimestre`;
    }
    setErro(null);
    onExportar(params);
  };

  const rotulo = `block text-xs font-semibold uppercase tracking-wider mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`;
  const campo = `w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-[#0974BA]/40 ${darkMode
    ? 'bg-slate-700/60 border-slate-600 text-slate-200 [color-scheme:dark]'
    : 'bg-white border-slate-300 text-slate-700'
  }`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exportar-registro-titulo"
      onClick={(e) => { if (e.target === e.currentTarget && !gerando) onFechar(); }}
    >
      <div className={`w-full max-w-lg rounded-2xl overflow-hidden max-h-[92vh] flex flex-col ${darkMode
        ? 'bg-slate-800 border border-slate-700'
        : 'bg-white shadow-xl'
      }`}>
        <div className="px-6 py-4 text-white" style={{ background: 'linear-gradient(135deg, #0974BA, #2E4974)' }}>
          <h2 id="exportar-registro-titulo" className="text-lg font-semibold">
            Exportar Registro de Aulas
          </h2>
          <p className="text-xs text-blue-100 mt-0.5">
            Escolha o período e os filtros. O PDF sai pronto para imprimir e assinar.
          </p>
        </div>

        <div className="p-6 space-y-5 overflow-y-auto">
          <div>
            <span className={rotulo}>Período</span>
            <div className={`flex gap-1 p-1 rounded-xl ${darkMode ? 'bg-slate-900/50' : 'bg-slate-100'}`}>
              {MODOS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => trocarModo(m.id)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${modo === m.id
                    ? 'text-white shadow'
                    : darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'
                  }`}
                  style={modo === m.id ? { background: 'linear-gradient(135deg, #0974BA, #2E4974)' } : {}}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {modo === 'bimestre' && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                {BIMESTRES_PADRAO.map((b) => (
                  <button
                    key={b.numero}
                    type="button"
                    onClick={() => escolherBimestre(b.numero)}
                    className={`py-2.5 rounded-lg text-sm font-semibold border transition-all ${bimestre === b.numero
                      ? 'border-[#0974BA] bg-[#0974BA]/10 text-[#0974BA]'
                      : darkMode
                        ? 'border-slate-600 text-slate-300 hover:border-slate-500'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {b.numero}º Bim
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label>
                  <span className={rotulo}>De</span>
                  <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className={campo} />
                </label>
                <label>
                  <span className={rotulo}>Até</span>
                  <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className={campo} />
                </label>
              </div>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Datas padrão da rede para {anoLetivo}. Se o calendário da escola for
                diferente, ajuste antes de gerar.
              </p>
            </div>
          )}

          {modo === 'mes' && (
            <label className="block">
              <span className={rotulo}>Mês</span>
              <input type="month" value={mes} max={hoje.slice(0, 7)} onChange={(e) => setMes(e.target.value)} className={campo} />
            </label>
          )}

          {modo === 'datas' && (
            <div className="grid grid-cols-2 gap-3">
              <label>
                <span className={rotulo}>De</span>
                <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className={campo} />
              </label>
              <label>
                <span className={rotulo}>Até</span>
                <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className={campo} />
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label>
              <span className={rotulo}>Disciplina</span>
              <select value={disciplina} onChange={(e) => setDisciplina(e.target.value)} className={campo}>
                {subjects.length > 1 && <option value="">Todas as disciplinas</option>}
                {subjects.map((s) => (
                  <option key={s.id} value={s.id}>{s.subject_name}</option>
                ))}
              </select>
            </label>
            <label>
              <span className={rotulo}>Situação</span>
              <select value={situacao} onChange={(e) => setSituacao(e.target.value)} className={campo}>
                {SITUACOES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </label>
          </div>

          {erro && (
            <p className="text-sm text-[#E83A3C] bg-[#E83A3C]/10 border border-[#E83A3C]/20 rounded-lg px-3 py-2">
              {erro}
            </p>
          )}
        </div>

        <div className={`px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t ${darkMode ? 'border-slate-700' : 'border-slate-100'}`}>
          <button
            type="button"
            onClick={onFechar}
            disabled={gerando}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-colors ${darkMode
              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            }`}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={exportar}
            disabled={gerando}
            className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${gerando
              ? 'bg-slate-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-[#E83A3C] to-[#C4292B] hover:shadow-lg hover:shadow-[#E83A3C]/25'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {gerando ? 'Gerando PDF...' : 'Gerar PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExportarRegistroAulasModal;
